import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Typography,
  Spinner,
  Chip,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
} from "@material-tailwind/react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { vmaDb } from "../../firebase-vma";
import { UserAuth } from "../../context/AuthContext";

const STATUS_COLORS = {
  new: "blue",
  "in-progress": "amber",
  completed: "teal",
  deleted: "red",
};

const functions = getFunctions();

function ValuationSubmissions() {
  const { user, valuationAdmin, isAdmin, delegateFor } = UserAuth();
  const canAssign = valuationAdmin || isAdmin;

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dialogView, setDialogView] = useState("details");
  const [users, setUsers] = useState([]);
  const [filterView, setFilterView] = useState("all");

  // Load enabled PlanePortal users
  useEffect(() => {
    if (!canAssign) return;
    const loadUsers = async () => {
      try {
        const getUserStatus = httpsCallable(functions, "getUserStatus");
        const result = await getUserStatus();
        const userList = (result.data || [])
          .filter((u) => !u.disabled)
          .map((u) => ({
            uid: u.userId,
            displayName: u.displayName || u.email,
            email: u.email,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        setUsers(userList);
      } catch (err) {
        console.error("Failed to load users:", err);
      }
    };
    loadUsers();
  }, [canAssign]);

  // Real-time submissions listener
  useEffect(() => {
    const q = query(
      collection(vmaDb, "submissions"),
      orderBy("submittedAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setSubmissions(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          submittedAt: d.data().submittedAt?.toDate?.() || null,
        }))
      );
      setLoading(false);
    });
    return unsub;
  }, []);

  const activeSubmissions = useMemo(() =>
    submissions.filter((s) => s.status !== "deleted"),
    [submissions]
  );

  // Check if a submission is assigned to the current user or someone they delegate for
  const isAssignedToMeOrMyAgent = useCallback((s) => {
    if (s.assignedTo === user?.uid) return true;
    if (delegateFor?.length > 0 && delegateFor.includes(s.assignedTo)) return true;
    return false;
  }, [user, delegateFor]);

  const filteredSubmissions = useMemo(() => {
    if (filterView === "mine") {
      return activeSubmissions.filter(isAssignedToMeOrMyAgent);
    }
    if (filterView === "unassigned") {
      return activeSubmissions.filter((s) => !s.assignedTo);
    }
    if (filterView === "in-progress") {
      return activeSubmissions.filter((s) => s.status === "in-progress");
    }
    if (filterView === "completed") {
      return activeSubmissions.filter((s) => s.status === "completed");
    }
    if (filterView === "deleted") {
      return submissions.filter((s) => s.status === "deleted");
    }
    return activeSubmissions;
  }, [submissions, activeSubmissions, filterView, isAssignedToMeOrMyAgent]);

  // Keep selected in sync with live data
  const selectedId = selected?.id;
  useEffect(() => {
    if (!selectedId) return;
    const live = submissions.find((s) => s.id === selectedId);
    if (live) {
      setSelected((prev) => {
        if (!prev) return prev;
        if (live.status !== prev.status || live.assignedTo !== prev.assignedTo || live.assignedName !== prev.assignedName) {
          return { ...live };
        }
        return prev;
      });
    }
  }, [submissions, selectedId]);

  const closeDialog = useCallback(() => {
    setSelected(null);
    setDialogView("details");
  }, []);

  const updateSubmission = useCallback(async (id, updates) => {
    await updateDoc(doc(vmaDb, "submissions", id), updates);
  }, []);

  const assignUser = async (submissionId, uid) => {
    const assignedUser = users.find((u) => u.uid === uid);
    const submission = submissions.find((s) => s.id === submissionId);
    const updates = {
      assignedTo: uid || null,
      assignedName: assignedUser?.displayName || null,
      assignedEmail: assignedUser?.email || null,
    };
    // Auto-transition to in-progress when assigning
    if (uid && submission?.status === "new") {
      updates.status = "in-progress";
    }
    await updateSubmission(submissionId, updates);
    setSelected((s) => (s ? { ...s, ...updates } : s));

    // Send email notification if assigning to someone
    if (uid && assignedUser?.email && submission) {
      try {
        const sendEmail = httpsCallable(functions, "sendValuationAssignmentEmail");
        await sendEmail({
          assigneeEmail: assignedUser.email,
          assigneeName: assignedUser.displayName,
          assigneeUid: uid,
          aircraft: {
            year: submission.year,
            make: submission.make,
            model: submission.model,
            registration: submission.registration,
            ownerName: submission.name,
            ownerEmail: submission.email,
            ownerPhone: submission.phone,
            ttsn: submission.ttsn,
            location: submission.location,
            sellerTimeline: submission.sellerTimeline,
          },
        });
      } catch (err) {
        console.warn("Failed to send assignment email:", err);
      }
    }
  };

  const getAssigneeName = (sub) => {
    if (!sub.assignedTo) return null;
    return sub.assignedName || users.find((u) => u.uid === sub.assignedTo)?.displayName || "Unknown";
  };

  const newCount = useMemo(() => activeSubmissions.filter((s) => s.status === "new").length, [activeSubmissions]);
  const myCount = useMemo(() => activeSubmissions.filter(isAssignedToMeOrMyAgent).length, [activeSubmissions, isAssignedToMeOrMyAgent]);

  // Non-admin users see their assignments + assignments for agents they delegate for
  const visibleSubmissions = useMemo(() =>
    canAssign ? filteredSubmissions : activeSubmissions.filter(isAssignedToMeOrMyAgent),
    [canAssign, filteredSubmissions, activeSubmissions, isAssignedToMeOrMyAgent]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mb-4">
        <Typography variant="h4" className="text-lma-dark font-montserrat text-xl md:text-2xl">
          Valuation Requests
        </Typography>
        <div className="flex items-center gap-2">
          {canAssign && (
            <Chip
              value={`${newCount} new`}
              color="blue"
              size="sm"
            />
          )}
          {myCount > 0 && (
            <Chip
              value={`${myCount} assigned to me`}
              color="amber"
              size="sm"
            />
          )}
        </div>
      </div>

      {/* Filter tabs - only for admins/valuationAdmin */}
      {canAssign && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {[
            { key: "all", label: "All" },
            { key: "stats", label: "Stats" },
            { key: "mine", label: "My Assignments" },
            { key: "unassigned", label: "Unassigned" },
            { key: "in-progress", label: "In Progress" },
            { key: "completed", label: "Completed" },
            ...(isAdmin ? [{ key: "deleted", label: "Deleted" }] : []),
          ].map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              variant={filterView === key ? "filled" : "outlined"}
              color={filterView === key ? "blue-gray" : "gray"}
              onClick={() => setFilterView(key)}
              className="text-xs shrink-0"
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      {/* Stats view */}
      {filterView === "stats" && canAssign && (
        <StatsPanel submissions={submissions} />
      )}

      {filterView !== "stats" && (
        visibleSubmissions.length === 0 ? (
          <Card>
            <CardBody className="text-center py-12">
              <Typography color="gray">
                {canAssign
                  ? filterView === "unassigned"
                    ? "No unassigned submissions."
                    : "No submissions yet. Submissions will appear here in real-time."
                  : "No valuation requests assigned to you yet."}
              </Typography>
            </CardBody>
          </Card>
        ) : (
          <div className="grid gap-0 border border-gray-200 bg-white">
            {visibleSubmissions.map((sub) => {
              const isUnread = sub.status === "new";
              return (
              <div
                key={sub.id}
                className={`cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors px-3 md:px-4 py-3 ${isUnread ? "bg-blue-50/40" : ""}`}
                onClick={() => setSelected(sub)}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2">
                  <div className="min-w-0 flex gap-3 items-start">
                    {isUnread && (
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                    )}
                    {!isUnread && (
                      <div className="w-2.5 h-2.5 mt-1.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <Typography className={`text-base md:text-lg truncate ${isUnread ? "font-bold text-lma-dark" : "font-normal text-lma-gray"}`}>
                        {sub.year} {sub.make} {sub.model}
                      </Typography>
                      <Typography className={`text-sm truncate ${isUnread ? "text-lma-dark font-medium" : "text-gray-500"}`}>
                        {sub.name} &middot; {sub.registration}
                      </Typography>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <Typography className="text-xs text-gray-400">
                          {sub.submittedAt
                            ? sub.submittedAt.toLocaleString()
                            : "Pending"}
                        </Typography>
                        {getAssigneeName(sub) && (
                          <Typography className="text-xs text-blue-600">
                            &rarr; {getAssigneeName(sub)}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap ml-5 md:ml-0">
                    {sub.sellerTimeline && (
                      <Chip
                        value={sub.sellerTimeline}
                        size="sm"
                        variant="ghost"
                        color="blue-gray"
                      />
                    )}
                    {sub.photoUrls?.length > 0 && (
                      <Chip
                        value={`${sub.photoUrls.length} photo${sub.photoUrls.length > 1 ? "s" : ""}`}
                        size="sm"
                        variant="ghost"
                        color="gray"
                      />
                    )}
                    <Chip
                      value={sub.status || "new"}
                      size="sm"
                      color={STATUS_COLORS[sub.status] || "blue"}
                    />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )
      )}

      {/* Detail Dialog */}
      <Dialog
        open={!!selected}
        handler={closeDialog}
        size="lg"
        dismiss={{ outsidePress: true }}
        animate={{ mount: { opacity: 1 }, unmount: { opacity: 0 } }}
      >
        {selected && (
          <>
            <DialogHeader className="pb-2 border-b-0 px-4 md:px-6 relative">
              <button
                onClick={closeDialog}
                className="absolute top-3 right-3 text-gray-400 hover:text-lma-dark w-8 h-8 flex items-center justify-center text-xl leading-none"
              >
                &times;
              </button>
              <div className="w-full pr-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                  <div className="min-w-0">
                    <Typography variant="h5" className="text-lma-dark font-semibold text-lg md:text-xl">
                      {selected.year} {selected.make} {selected.model}
                    </Typography>
                    <Typography className="text-sm text-lma-gray mt-0.5">
                      {selected.registration && <span className="font-mono font-semibold">{selected.registration}</span>}
                      {selected.location && <span> · {selected.location}</span>}
                    </Typography>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {selected.sellerTimeline && (
                      <Chip value={selected.sellerTimeline} size="sm" variant="ghost" color="gray" />
                    )}
                    <Chip
                      value={selected.status || "new"}
                      color={STATUS_COLORS[selected.status] || "blue"}
                      size="sm"
                    />
                  </div>
                </div>

                {/* Assignment bar */}
                <div className="mt-3">
                  {canAssign ? (
                    <select
                      value={selected.assignedTo || ""}
                      onChange={(e) => assignUser(selected.id, e.target.value)}
                      disabled={selected.status === "completed"}
                      className="w-full border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:border-lma-gold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Unassigned</option>
                      {users.map((u) => (
                        <option key={u.uid} value={u.uid}>
                          {u.displayName} ({u.email})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Typography className="text-sm text-lma-gray">
                      {selected.assignedName ? `Assigned to ${selected.assignedName}` : "Unassigned"}
                    </Typography>
                  )}
                </div>
              </div>
            </DialogHeader>

            <DialogBody className="max-h-[65vh] overflow-y-auto pt-4 px-4 md:px-6">
              {dialogView === "photos" && selected.photoUrls?.length > 0 ? (
                <div>
                  <button
                    onClick={() => setDialogView("details")}
                    className="flex items-center gap-1 text-sm text-lma-gray hover:text-lma-dark mb-4 font-medium"
                  >
                    &larr; Back to details
                  </button>
                  <div className="space-y-4">
                    {selected.photoUrls.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={url}
                          alt={`Aircraft ${i + 1} of ${selected.photoUrls.length}`}
                          className="w-full object-contain bg-gray-900 max-h-[50vh]"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <>

              {/* Contact & Owner */}
              <div className="mb-5 bg-gray-50 p-4 border border-gray-100">
                <SectionHeading>Contact</SectionHeading>
                <div className="space-y-2">
                  <Field label="Name" value={selected.name} />
                  <Field label="Email" value={selected.email} />
                  <Field label="Phone" value={selected.phone} />
                </div>
              </div>

              {/* Aircraft core info in a compact grid */}
              <div className="mb-5">
                <SectionHeading>Aircraft</SectionHeading>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
                  <FieldStacked label="Make" value={selected.make} />
                  <FieldStacked label="Model" value={selected.model} />
                  <FieldStacked label="Year" value={selected.year} />
                  <FieldStacked label="Registration" value={selected.registration} />
                  <FieldStacked label="Total Time Airframe" value={selected.ttsn} />
                  <FieldStacked label="Total Time Engine(s)" value={selected.totalTimeEngines} />
                  <FieldStacked label="Total Cycles" value={selected.totalCycles} />
                  <FieldStacked label="Location" value={selected.location} />
                </div>
                {selected.damageHistory && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-lma-gray font-semibold uppercase tracking-wide">Damage History</span>
                      <Chip
                        value={selected.damageHistory}
                        size="sm"
                        color={selected.damageHistory === "Yes" ? "red" : "green"}
                        variant="ghost"
                      />
                    </div>
                    {selected.damageHistory === "Yes" && selected.damageHistoryDetails && (
                      <Typography className="text-sm text-lma-dark mt-1">{selected.damageHistoryDetails}</Typography>
                    )}
                  </div>
                )}
              </div>

              {/* Two-column: Maintenance + Specifications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <SectionHeading>Maintenance</SectionHeading>
                  <div className="space-y-2">
                    <Field label="Warranty Exp." value={selected.warrantyExpiration} />
                    <Field label="Last Inspection" value={selected.lastAnnualDate} />
                    <Field label="Inspection Type" value={selected.lastInspectionType} />
                    <Field label="Program" value={selected.programEnrollment} />
                    <Field label="Complete Logs" value={selected.completeLogbooks} />
                  </div>
                </div>

                <div>
                  <SectionHeading>Specifications</SectionHeading>
                  <div className="space-y-2">
                    <Field label="Avionics" value={selected.avionics} />
                    <Field label="Engine" value={selected.engine} />
                    <Field label="Exterior" value={selected.exteriorColour} />
                    <Field label="Interior" value={selected.interiorColour} />
                    <Field label="A/C" value={selected.ac} />
                  </div>
                  {selected.additionalFeatures && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs text-lma-gray font-semibold uppercase tracking-wide">Additional Features</span>
                      <Typography className="text-sm text-lma-dark mt-1">{selected.additionalFeatures}</Typography>
                    </div>
                  )}
                </div>
              </div>

              {/* Photo thumbnails */}
              {selected.photoUrls?.length > 0 && (
                <div className="mb-5">
                  <SectionHeading>Photos</SectionHeading>
                  <div className="flex gap-2 flex-wrap">
                    {selected.photoUrls.map((url, i) => (
                      <button
                        key={i}
                        onClick={() => setDialogView("photos")}
                        className="shrink-0 border-2 border-transparent hover:border-lma-gold transition-colors"
                      >
                        <img
                          src={url}
                          alt={`Aircraft ${i + 1}`}
                          className="h-20 w-28 md:h-16 md:w-24 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setDialogView("photos")}
                    className="text-xs text-lma-gold font-medium mt-1 hover:underline"
                  >
                    View all photos &rarr;
                  </button>
                </div>
              )}

              {/* Meta */}
              <div className="text-xs text-lma-gray border-t border-gray-200 pt-3">
                Submitted{" "}
                {selected.submittedAt
                  ? selected.submittedAt.toLocaleString()
                  : "Pending"}
                {selected.source && ` · ${selected.source}`}
              </div>
              </>
              )}
            </DialogBody>
            <DialogFooter className="gap-2">
              {selected.status !== "completed" && (
                <>
                  <Button
                    variant="outlined"
                    size="sm"
                    color="amber"
                    onClick={() => {
                      updateSubmission(selected.id, { status: "in-progress" });
                      setSelected((s) => ({ ...s, status: "in-progress" }));
                    }}
                  >
                    In Progress
                  </Button>
                  <Button
                    size="sm"
                    color="teal"
                    onClick={() => {
                      updateSubmission(selected.id, { status: "completed" });
                      setSelected((s) => ({ ...s, status: "completed" }));
                    }}
                  >
                    Completed
                  </Button>
                </>
              )}
              {selected.status === "completed" && isAdmin && (
                <Button
                  variant="outlined"
                  size="sm"
                  color="amber"
                  onClick={() => {
                    updateSubmission(selected.id, { status: "in-progress" });
                    setSelected((s) => ({ ...s, status: "in-progress" }));
                  }}
                >
                  Reopen
                </Button>
              )}
              {canAssign && selected.status !== "deleted" && (
                <Button
                  variant="outlined"
                  size="sm"
                  color="red"
                  onClick={async () => {
                    if (window.confirm("Are you sure you want to delete this submission?")) {
                      const id = selected.id;
                      setSelected(null);
                      await updateSubmission(id, { status: "deleted" });
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              {isAdmin && selected.status === "deleted" && (
                <Button
                  variant="outlined"
                  size="sm"
                  color="amber"
                  onClick={() => {
                    updateSubmission(selected.id, { status: "new" });
                    setSelected((s) => ({ ...s, status: "new" }));
                  }}
                >
                  Restore
                </Button>
              )}
              <Button
                variant="text"
                size="sm"
                onClick={closeDialog}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-sm font-bold text-lma-dark uppercase tracking-wide mb-2 pb-1 border-b-2 border-lma-gold">
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex text-sm leading-normal py-0.5">
      <span className="text-lma-gray font-medium w-32 shrink-0">{label}</span>
      <span className={value ? "text-lma-dark font-medium" : "text-gray-400 italic"}>{value || "—"}</span>
    </div>
  );
}

function FieldStacked({ label, value }) {
  return (
    <div>
      <div className="text-xs text-lma-gray font-medium">{label}</div>
      <div className={`text-sm font-semibold ${value ? "text-lma-dark" : "text-gray-400 italic"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function StatsPanel({ submissions }) {
  const active = submissions.filter((s) => s.status !== "deleted");
  const assigned = active.filter((s) => s.assignedTo);

  // Group by assignee
  const byUser = {};
  assigned.forEach((s) => {
    const uid = s.assignedTo;
    if (!byUser[uid]) {
      byUser[uid] = { uid, name: s.assignedName || "Unknown", inProgress: 0, completed: 0, total: 0 };
    }
    byUser[uid].total++;
    if (s.status === "in-progress") byUser[uid].inProgress++;
    if (s.status === "completed") byUser[uid].completed++;
  });

  const rows = Object.values(byUser).sort((a, b) => b.total - a.total);
  const totals = rows.reduce(
    (acc, r) => ({ inProgress: acc.inProgress + r.inProgress, completed: acc.completed + r.completed, total: acc.total + r.total }),
    { inProgress: 0, completed: 0, total: 0 }
  );
  const unassignedCount = active.filter((s) => !s.assignedTo).length;

  return (
    <div>
      {/* Summary badges */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="bg-white shadow-sm px-4 py-3 flex items-center gap-2">
          <Typography className="text-sm text-gray-600">Total</Typography>
          <Chip value={String(totals.total)} size="sm" color="blue-gray" />
        </div>
        <div className="bg-white shadow-sm px-4 py-3 flex items-center gap-2">
          <Typography className="text-sm text-gray-600">In Progress</Typography>
          <Chip value={String(totals.inProgress)} size="sm" color="amber" />
        </div>
        <div className="bg-white shadow-sm px-4 py-3 flex items-center gap-2">
          <Typography className="text-sm text-gray-600">Completed</Typography>
          <Chip value={String(totals.completed)} size="sm" color="teal" />
        </div>
        {unassignedCount > 0 && (
          <div className="bg-white shadow-sm px-4 py-3 flex items-center gap-2">
            <Typography className="text-sm text-gray-600">Unassigned</Typography>
            <Chip value={String(unassignedCount)} size="sm" color="blue" />
          </div>
        )}
      </div>

      {/* Per-user table */}
      <Card>
        <CardBody>
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-2 font-semibold text-lma-dark">User</th>
                <th className="py-2 font-semibold text-lma-dark text-center">In Progress</th>
                <th className="py-2 font-semibold text-lma-dark text-center">Completed</th>
                <th className="py-2 font-semibold text-lma-dark text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.uid} className="border-b border-gray-100">
                  <td className="py-2 text-gray-800">{r.name}</td>
                  <td className="py-2 text-center">
                    <Chip value={String(r.inProgress)} size="sm" color="amber" variant="ghost" className="inline-block" />
                  </td>
                  <td className="py-2 text-center">
                    <Chip value={String(r.completed)} size="sm" color="teal" variant="ghost" className="inline-block" />
                  </td>
                  <td className="py-2 text-center font-medium">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

export default ValuationSubmissions;
